/* Child tables: the rows inside an invoice, an order, a BOM.
 *
 * 432 fields in the in-scope surface are Tables, and they are where the money
 * actually is — an invoice is its line items. Two decisions:
 *
 *   - **A grid on a desktop, cards on a phone.** A twelve-column grid scrolled
 *     sideways on a 390px screen is unusable, and the design says cards, not a
 *     table turned on its side (S3.5).
 *   - **Only the columns worth showing.** A child DocType has 30-odd fields
 *     and `in_list_view` says which half-dozen belong in the grid; the rest
 *     are behind the row expander.
 */
import { useState } from 'react'
import type { Control, ControlProps } from './kit'
import { Field } from './kit'
import type { DocField, Doc } from '../types'
import { LAYOUT_TYPES } from '../types'

export type RenderControl = (props: ControlProps) => React.ReactNode


export function makeTableControl(renderControl: RenderControl, childFields: (doctype: string) => DocField[]): Control {
  return function TableControl(p) {
    const fields = childFields(p.field.options ?? '')
    const rows = Array.isArray(p.value) ? (p.value as Doc[]) : []
    const [expanded, setExpanded] = useState<number | null>(null)

    const columns = fields.filter((f) => f.in_list_view && !f.hidden && !LAYOUT_TYPES.has(f.fieldtype))
    const shown = columns.length ? columns : fields.filter((f) => !LAYOUT_TYPES.has(f.fieldtype)).slice(0, 5)

    const setRow = (index: number, changes: Doc) => {
      const next = rows.map((r, i) => (i === index ? { ...r, ...changes } : r))
      p.onChange(next)
    }
    const addRow = () => {
      p.onChange([...rows, { __islocal: 1, idx: rows.length + 1 }])
      setExpanded(rows.length)
    }
    const removeRow = (index: number) => {
      p.onChange(rows.filter((_, i) => i !== index).map((r, i) => ({ ...r, idx: i + 1 })))
      setExpanded(null)
    }

    const cell = (row: Doc, index: number, f: DocField) =>
      renderControl({
        field: f, value: row[f.fieldname], doc: row, parent: p.doc,
        readOnly: p.readOnly || Boolean(f.read_only), required: false,
        onChange: (v) => setRow(index, { [f.fieldname]: v }),
        currency: p.currency, bare: true,
      })

    return (
      <Field field={p.field} required={p.required} error={p.error}>
        <div className="card" style={{ overflow: 'hidden' }}>
          {/* Desktop: a real grid. */}
          <div className="erp-grid-wide" style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 34 }} aria-label="Row" />
                  {shown.map((f) => (
                    <th key={f.fieldname} className={isNumeric(f) ? 'num' : undefined}>{f.label || f.fieldname}</th>
                  ))}
                  <th style={{ width: 40 }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={shown.length + 2} className="muted small" style={{ textAlign: 'center', height: 56 }}>
                    No rows yet.
                  </td></tr>
                ) : rows.map((row, index) => (
                  <tr key={index} style={{ cursor: 'default' }}>
                    <td className="muted small num">{index + 1}</td>
                    {shown.map((f) => (
                      <td key={f.fieldname} className={isNumeric(f) ? 'num' : undefined}
                          style={{ padding: '2px 6px', minWidth: widthOf(f) }}>
                        {cell(row, index, f)}
                      </td>
                    ))}
                    <td>
                      <div className="row" style={{ gap: 2 }}>
                        <button type="button" className="btn btn-quiet btn-sm"
                                aria-expanded={expanded === index}
                                aria-label={`All fields for row ${index + 1}`}
                                onClick={() => setExpanded(expanded === index ? null : index)}>⋯</button>
                        {p.readOnly ? null : (
                          <button type="button" className="btn btn-quiet btn-sm btn-danger"
                                  aria-label={`Remove row ${index + 1}`}
                                  onClick={() => removeRow(index)}>×</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Phone: one card per row, not the same table scrolled sideways. */}
          <div className="erp-grid-narrow">
            {rows.length === 0 ? <div className="muted small" style={{ padding: 16 }}>No rows yet.</div> : null}
            {rows.map((row, index) => (
              <div key={index} style={{ padding: 12, borderBottom: '1px solid var(--line)' }}>
                <div className="row-between" style={{ marginBottom: 8 }}>
                  <strong className="small">Row {index + 1}</strong>
                  {p.readOnly ? null : (
                    <button type="button" className="btn btn-quiet btn-sm btn-danger"
                            onClick={() => removeRow(index)} aria-label={`Remove row ${index + 1}`}>Remove</button>
                  )}
                </div>
                <div className="stack">{shown.map((f) => <div key={f.fieldname}>{
                  renderControl({
                    field: f, value: row[f.fieldname], doc: row, parent: p.doc,
                    readOnly: p.readOnly || Boolean(f.read_only), required: false,
                    onChange: (v) => setRow(index, { [f.fieldname]: v }), currency: p.currency,
                  })
                }</div>)}</div>
              </div>
            ))}
          </div>

          {expanded !== null && rows[expanded] ? (
            <div style={{ padding: 14, borderTop: '1px solid var(--line)', background: 'var(--sunk)' }}>
              <div className="row-between" style={{ marginBottom: 10 }}>
                <strong className="small">Row {expanded + 1} — all fields</strong>
                <button type="button" className="btn btn-quiet btn-sm" onClick={() => setExpanded(null)}>Close</button>
              </div>
              <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
                {fields.filter((f) => !LAYOUT_TYPES.has(f.fieldtype) && !f.hidden).map((f) => (
                  <div key={f.fieldname}>
                    {renderControl({
                      field: f, value: rows[expanded]![f.fieldname], doc: rows[expanded]!, parent: p.doc,
                      readOnly: p.readOnly || Boolean(f.read_only), required: Boolean(f.reqd),
                      onChange: (v) => setRow(expanded, { [f.fieldname]: v }), currency: p.currency,
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {p.readOnly ? null : (
            <div style={{ padding: 8, borderTop: '1px solid var(--line)' }}>
              <button type="button" className="btn btn-sm" onClick={addRow}>+ Add row</button>
            </div>
          )}
        </div>
      </Field>
    )
  }
}

const isNumeric = (f: DocField) => ['Int', 'Float', 'Currency', 'Percent'].includes(f.fieldtype)
const widthOf = (f: DocField) =>
  f.fieldtype === 'Check' ? 60 : isNumeric(f) ? 120 : f.fieldtype === 'Link' ? 180 : 150
